import * as React from 'react';
import { UserRepository } from '../../MedicareProAPI/Repositories/User/UserRepository';
import type { UserConnecture } from '../../MedicareProAPI/Entities/User/UserConnecture';
import { UserPersonalTokenRepository } from '../../MedicareProAPI/Repositories/User/UserPersonalTokenRepository';
import type { UserSunfire } from '../../MedicareProAPI/Entities/User/UserSunfire';
import { ConnectureEnrollButton } from '../Buttons/ConnectureEnrollButton';
import { SunFireEnrollButton } from '../Buttons/SunFireEnrollButton';
import { HealthSherpaMedicareEnrollButton } from '../Buttons/HealthSherpaMedicareEnrollButton';
import StyledModal from '../Components/StyledModal';

import { FeatureFlag } from '../../MedicareProAPI/Utilities/FeatureFlag';

interface AgentEnrollmentPanelProps {
  userId: number;
  userAccessToken: string;
  agencyId: number | null;
  uplineId: number | null;
  absoluteUplineId: number | null;
  indId: number;
  stack?: boolean;
}

/**
 * Panel for managing enrollment buttons on profile page
 *
 * @component
 * @author Andrew Brown <abrown@medicareprocrm.com>
 * @date Created: 2025-10-12
 * @lastUpdated
 * @version 1.1.0
 */
export default function AgentEnrollmentPanel({
  userId,
  userAccessToken,
  agencyId,
  uplineId,
  absoluteUplineId,
  indId,
  stack,
}: AgentEnrollmentPanelProps): React.JSX.Element {
  const [userConnecture, setUserConnecture] = React.useState<Array<UserConnecture>>([]);
  const [userHealthSherpa, setUserHealthSherpa] = React.useState<boolean>();
  const [userSunFire, setUserSunfire] = React.useState<Array<UserSunfire>>([]);
  const [displayInfoModal, setDisplayInfoModal] = React.useState<boolean>(false);

  const [allFetched, setAllFetched] = React.useState<boolean>(false);

  const canConnecture = React.useMemo(
    () => FeatureFlag.canAccessConnectureEnroll(Number(userId)),
    [userId]
  );

  const userRepository = React.useMemo(() => {
    const r = new UserRepository();
    r.setAccessToken(userAccessToken);
    return r;
  }, [userAccessToken]);

  const userPersonalTokenRepository = React.useMemo(() => {
    const r = new UserPersonalTokenRepository();
    r.setAccessToken(userAccessToken);
    return r;
  }, [userAccessToken]);

  const infoData = [
    {
      title: 'Connecture For Agencies',
      body: 'If your agency is in MedicarePRO and they have integrated their Connecture instance, they manage your connection.',
      icon: 'connectureicon.png',
    },
    {
      title: 'Connecture For Agents (PCEA)',
      body: (
        <>
          Connect your PCEA account{' '}
          <a href="https://www.medicareproapp.com/agent/settings/index/integration">here.</a>
        </>
      ),
      icon: 'connectureicon.png',
    },
    {
      title: 'HealthSherpa For Medicare',
      body: (
        <>
          Connect your HSFM account{' '}
          <a href="https://www.medicareproapp.com/agent/settings/index/integration">here.</a>
        </>
      ),
      icon: 'healthsherpamedicareicon.png',
    },
    {
      title: 'SunFire Matrix',
      body: (
        <>
          If your Org permits, you can connect your CRM to your SunFire Matrix account. Click{' '}
          <a href="https://www.medicareproapp.com/agent/settings/index/integration">here</a>
        </>
      ),
      icon: 'sunfireicon.png',
    },
  ];

  React.useEffect(() => {
    let alive = true;
    console.log('in effect');
    (async () => {
      try {
        const [userResp, tokenResp] = await Promise.all([
          userRepository.getUser(userId, { include: ['userConnecture', 'userSunfire'] }),
          userPersonalTokenRepository.getUserPersonalToken(userId, 'healthsherpamedicare'),
        ]);

        if (!alive) return;

        setUserConnecture(userResp?.getUserConnecture?.()?.getItems?.() ?? []);
        setUserSunfire(userResp?.getUserSunfire?.()?.getItems?.() ?? []);
        setUserHealthSherpa(Boolean(tokenResp?.exists));
      } catch (e) {
        console.error('Init fetch failed:', e);
        if (!alive) return;

        setUserConnecture([]);
        setUserSunfire([]);
        setUserHealthSherpa(false);
      } finally {
        if (alive) setAllFetched(true);
      }
    })();

    return () => {
      alive = false;
    };
  }, [userId, userRepository, userPersonalTokenRepository]);

  return (
    <>
      {userConnecture.length > 0 &&
        canConnecture && ( // conditional feature flag for testing
          <ConnectureEnrollButton
            userId={userId}
            userAccessToken={userAccessToken}
            thisUserConnecture={userConnecture}
            indId={indId}
            stack={stack}
          />
        )}

      {userSunFire.length > 0 && (
        <SunFireEnrollButton
          userId={userId}
          userAccessToken={userAccessToken}
          thisUserSunFire={userSunFire}
          indId={indId}
          stack={stack}
        />
      )}

      {userHealthSherpa && (
        <HealthSherpaMedicareEnrollButton
          userId={userId}
          userAccessToken={userAccessToken}
          thisHealthSherpa={userHealthSherpa}
          indId={indId}
          stack={stack}
        />
      )}

      {userConnecture.length === 0 &&
        userSunFire.length === 0 &&
        !userHealthSherpa &&
        allFetched && (
          <div
            className="btn btn-sm btn-success mb-3 mr-2  float-right"
            onClick={() => setDisplayInfoModal(true)}
            role="button"
          >
            <i className="fal fa-rocket ml-r"></i> Enroll
          </div>
        )}

      {displayInfoModal && (
        <StyledModal
          key={`info-modal`}
          size="lg"
          title="Enrollment Options"
          footerCloseButton
          show={true}
          onClose={() => setDisplayInfoModal(false)}
        >
          {infoData.map((item, index) => {
            return (
              <div className="rounded mb-3 bg-contrast p-2" key={`info-item-${index}`}>
                <div className="font-weight-bold">
                  <img
                    style={{ height: '20px' }}
                    alt="Connecture"
                    src={`https://medicareprocrm-cdn.s3.us-east-2.amazonaws.com/public/img/site/${item.icon}`}
                  />
                  <span className="ml-2">{item.title}</span>
                </div>
                <div className="mt-2">{item.body}</div>
              </div>
            );
          })}
        </StyledModal>
      )}
    </>
  );
}
